// The words and rules Settings, Users shows, kept out of the components so they are tested on their own.
// Copy from the Settings board and spec C1, C6, C14; lines marked "proposed" are not on the board.
import type { UserRow, UserStatus } from '../auth/api';
import { roleLabels, type Role, type RoleCode } from '../roles';

export const usersCopy = {
  title: 'Users',
  addUser: 'Add user',
  search: 'Search users',
  noMatch: 'No users match.',
  noneWithFilter: 'No users with this filter.',
  clearSearch: 'Clear search',
  note: 'Out today: new renewals skip the person in round-robin. Their open renewals stay with them. Reassign from the list if needed. A new user shows as Invited until they set a password, and gets no assignments.',
  newSub: 'They get an email invitation to set a password.',
  account: 'Account',
  usedInSheet: 'Used in sheet comments.',
  validEmail: 'Enter a valid email address.',
  // proposed
  emailTaken: 'This email already has an account.',
  // proposed
  emailFixed: 'The email is the sign-in name. To change it, add the person again.',
  roleNoteManager: 'Only the Admin can make someone a Manager or Admin.',
  roleNoteSelf:
    'You cannot change your own role. Only the Admin can make someone a Manager or Admin.',
  roleNoteAdminSelf: 'You cannot change your own role.',
  invitedLine: 'Invited. They have not set a password yet, so they get no assignments.',
  resendInvitation: 'Resend invitation',
  timeAway: 'Time away',
  outToday: 'Out today',
  // proposed
  outTodayNote: 'Until midnight. New renewals skip them; their open renewals stay with them.',
  datedAway: 'Vacation and leave with dates',
  phase2: 'Phase 2',
  signIn: 'Sign-in',
  adminOnly: 'Admin only',
  twoStep: 'Two-step sign-in',
  twoStepLine: (email: string) => `Code by email to ${email}. On for every user. No backup codes.`,
  resetPassword: 'Reset password',
  signOutAll: 'Sign out of all sessions',
  resetSent: (email: string) => `Reset link sent to ${email}.`,
  signedOutOf: (n: number) => `Signed out of ${n} ${n === 1 ? 'session' : 'sessions'}.`,
  history: 'History',
  historyCount: (n: number) => `History (${n})`,
  hideHistory: 'Hide history',
  noHistory: 'No changes yet.',
  cancel: 'Cancel',
  deactivate: 'Deactivate',
  reactivate: 'Reactivate',
  unsaved: 'Unsaved changes',
  save: 'Save',
  saveAndInvite: 'Save and invite',
  invitationSent: (email: string) => `Invitation sent to ${email}.`,
  saved: (name: string) => `${name} saved.`,
  reactivated: (name: string) => `${name} reactivated.`,
  // proposed
  deactivated: (name: string) => `${name} deactivated.`,
  deactivateTitle: (name: string) => `Deactivate ${name}?`,
  deactivateLine: (name: string) => `${name} can no longer sign in. You can reactivate them later.`,
  discardTitle: (name: string) => `Discard changes to ${name}?`,
  discardLine: 'Your changes are not saved.',
  keepEditing: 'Keep editing',
  discard: 'Discard',
  // proposed
  somethingWrong: 'Something went wrong. Try again.',
} as const;

/** The role cards in the editor, with the line under each name (Settings board). */
export const roleCards: { code: RoleCode; sub: string }[] = [
  { code: 'renewal_va', sub: 'Works renewals' },
  { code: 'account_executive', sub: 'Takes handoffs' },
  { code: 'manager', sub: 'Runs the team' },
  { code: 'admin', sub: 'Sets up the portal' },
];

/** The role filter chips: All, then each role. */
export const roleFilters: { key: 'all' | RoleCode; label: string }[] = [
  { key: 'all', label: 'All' },
  ...roleCards.map((r) => ({ key: r.code, label: roleLabels[r.code] })),
];

const statusPills: Record<UserStatus, [string, string]> = {
  active: ['Active', 'pill f-ok'],
  out_today: ['Out today', 'pill u-amber'],
  invited: ['Invited', 'pill t-grey'],
  inactive: ['Inactive', 'pill t-grey'],
};

export const statusLabel = (s: UserStatus) => statusPills[s][0];
export const statusClass = (s: UserStatus) => statusPills[s][1];

/** The Admin works on everyone; a Manager on Renewal VAs and Account Executives only. */
export function managesRole(actor: Role, target: RoleCode): boolean {
  if (actor === 'Admin') return true;
  return actor === 'Manager' && (target === 'renewal_va' || target === 'account_executive');
}

const newYork = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', ...opts });
const dayKey = newYork({ year: 'numeric', month: '2-digit', day: '2-digit' });
const monthDay = newYork({ month: 'short', day: 'numeric' });
const time = newYork({ hour: '2-digit', minute: '2-digit', hour12: false });

/** "Today 08:52", "Nov 11 17:04" or "Never", in New York time as the kit writes times. */
export function lastSignIn(iso: string | null, now = new Date()): string {
  if (!iso) return 'Never';
  const at = new Date(iso);
  const day = dayKey.format(at) === dayKey.format(now) ? 'Today' : monthDay.format(at);
  return `${day} ${time.format(at)}`;
}

export function matchesSearch(user: UserRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [user.name, user.email, user.initials, roleLabels[user.role]].some((v) =>
    v.toLowerCase().includes(q),
  );
}

export const looksLikeEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
export const validInitials = (value: string) => /^[A-Za-z]{1,3}$/.test(value.trim());

/** "Showing 7 of 9". */
export const showing = (shown: number, total: number) => `Showing ${shown} of ${total}`;
